"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import {
  uploadAvatarMedia,
  type UploadedAvatarMedia,
} from "@/lib/avatar-media-upload";
import { Badge, Button, Card, Spinner } from "./ui";

/**
 * "Create your own avatar" 素材区：上传本人照片/出镜视频，
 * 当前用户自己的出镜素材，使用现有 InfiniteTalk 流程。
 */

interface Upload extends UploadedAvatarMedia {
  ownedByCurrentUser?: boolean;
}

function fmtSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

export function AvatarUploads() {
  const [items, setItems] = useState<Upload[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const photoInput = useRef<HTMLInputElement>(null);
  const videoInput = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch("/api/avatars/uploads", { cache: "no-store", credentials: "same-origin" });
      if (r.ok) {
        const uploads = (await r.json()) as Upload[];
        setItems(uploads.filter((upload) => upload.ownedByCurrentUser === true));
      } else throw new Error("Could not load your photos. Please retry.");
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not load your photos. Please retry.");
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function upload(files: FileList | null) {
    if (!files?.length) return;
    if (!consentAccepted) {
      setError("Confirm that you have permission to animate this person first");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      for (const file of Array.from(files)) {
        await uploadAvatarMedia(file, consentAccepted);
      }
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setBusy(false);
      setConsentAccepted(false);
      if (photoInput.current) photoInput.current.value = "";
      if (videoInput.current) videoInput.current.value = "";
    }
  }

  async function updateConsent(id: string, accepted: boolean) {
    setBusy(true);
    setError(null);
    try {
      const response = await fetch(`/api/avatars/uploads/${id}`, {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ consentAccepted: accepted }),
      });
      if (!response.ok) {
        const body = (await response.json().catch(() => ({}))) as {
          error?: string;
        };
        throw new Error(body.error ?? "Could not update consent");
      }
      await load();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Could not update consent",
      );
    } finally {
      setBusy(false);
    }
  }

  async function remove(id: string) {
    setItems((xs) => xs?.filter((x) => x.id !== id) ?? null);
    await fetch(`/api/avatars/uploads/${id}`, { method: "DELETE" }).catch(
      () => {},
    );
    void load();
  }

  return (
    <section className="mt-6">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <h2 className="text-sm font-medium text-muted">
          My photos & videos
        </h2>
        <Badge>My avatar media</Badge>
      </div>

      <Card className="p-5">
        <p className="text-[13px] leading-6 text-muted">
          Upload a clear front-facing photo or a short talking video for an
          InfiniteTalk avatar. Only media with your explicit permission can be
          selected in Create.
        </p>

        <label className="mt-4 flex items-start gap-3 rounded-xl border border-line bg-white/[0.03] p-3 text-[12px] leading-5 text-muted">
          <input
            type="checkbox"
            className="mt-1 accent-[var(--accent)]"
            checked={consentAccepted}
            onChange={(event) => setConsentAccepted(event.target.checked)}
          />
          <span>
            I confirm that I am the person shown, or I have that person&apos;s
            permission to upload and animate their likeness with AI.
          </span>
        </label>

        <div className="mt-4 flex flex-wrap gap-2.5">
          <input
            ref={photoInput}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
            multiple
            hidden
            onChange={(e) => void upload(e.target.files)}
          />
          <input
            ref={videoInput}
            type="file"
            accept="video/mp4,video/quicktime"
            hidden
            onChange={(e) => void upload(e.target.files)}
          />
          <Button
            size="sm"
            disabled={busy || !consentAccepted}
            onClick={() => photoInput.current?.click()}
          >
            {busy ? <Spinner /> : "＋ Add photos"}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            disabled={busy || !consentAccepted}
            onClick={() => videoInput.current?.click()}
          >
            {busy ? <Spinner /> : "＋ Add video"}
          </Button>
          <span className="self-center text-[11px] text-muted">
            iPhone HEIC/HEIF, JPEG/PNG/WebP ≤ 10MB · MP4/MOV ≤ 200MB
          </span>
        </div>

        {error && (
          <div role="alert" className="mt-3 text-[13px] text-[#ffb3b3]">{error} <button type="button" className="underline" onClick={() => { setError(null); void load(); }}>Retry loading</button></div>
        )}

        {items === null && !error ? (
          <div className="mt-5 grid place-items-center py-6 text-muted">
            <Spinner className="size-5" />
          </div>
        ) : items && items.length > 0 ? (
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {items.map((u) => (
              <div
                key={u.id}
                className="group relative overflow-hidden rounded-xl border border-line bg-white/[0.03]"
              >
                {u.kind === "photo" ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={u.url}
                    alt={u.filename}
                    className="h-32 w-full object-cover"
                  />
                ) : (
                  <video
                    src={u.url}
                    className="h-32 w-full bg-black object-cover"
                    muted
                    playsInline
                    controls
                    preload="metadata"
                  />
                )}
                <div className="flex items-center justify-between gap-2 px-2.5 py-2">
                  <div className="min-w-0">
                    <div className="truncate text-[11px]">{u.filename}</div>
                    <div className="text-[10px] text-muted">
                      {u.kind === "photo" ? "Photo" : "Video"} ·{" "}
                      {fmtSize(u.size)}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => void remove(u.id)}
                    className="rounded-md px-1.5 py-0.5 text-muted transition hover:bg-white/10 hover:text-foreground"
                    aria-label={`Delete ${u.filename}`}
                  >
                    ✕
                  </button>
                </div>
                <div className="flex items-center justify-between gap-2 border-t border-line px-2.5 py-2">
                  <Badge tone={u.consentStatus === "approved" ? "success" : "warning"}>
                    {u.consentStatus === "approved"
                      ? "Consent approved"
                      : "Consent required"}
                  </Badge>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() =>
                      void updateConsent(u.id, u.consentStatus !== "approved")
                    }
                    className="text-[10px] text-muted transition hover:text-foreground disabled:opacity-50"
                  >
                    {u.consentStatus === "approved" ? "Revoke" : "Confirm rights"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : items ? (
          <div className="mt-5 rounded-xl border border-dashed border-line px-4 py-6 text-center text-[12px] text-muted">
            No material yet — add photos or a talking video to get your custom
            avatar ready.
          </div>
        ) : null}
      </Card>
    </section>
  );
}
